import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { fromPromise } from 'rxjs/internal-compatibility';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { toasterService, retryBackoff } from '@hevoio/hevo-ui-toolkit';
import { unauthorisedEventService } from '../../../core/services/unauthorised-event.service';
import { DEFAULT_NETWORK_REQ_OPTIONS } from '../constants/request-options';
import { NetworkRequestOptions, NetworkRequestUIOptions } from '../interfaces/request-options';
import { getErrorMessageFromAxiosError } from '../utils';

const BACKOFF_MAX_DELAY = 30000;
const BACKOFF_INIT_DELAY = 1000;

class HermesCourierService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<T = any, D = any>(url: string, options: NetworkRequestOptions = {}) {
    return this.sendRequest<T, D>(options, (axiosConfig: AxiosRequestConfig) =>
      axios.get(url, axiosConfig)
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<T = any, D = any>(url: string, data: D, options: NetworkRequestOptions = {}) {
    return this.sendRequest<T, D>(options, (axiosConfig: AxiosRequestConfig) =>
      axios.post(url, data, axiosConfig)
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<T = any, D = any>(url: string, data: D, options: NetworkRequestOptions = {}) {
    return this.sendRequest<T, D>(options, (axiosConfig: AxiosRequestConfig) =>
      axios.put(url, data, axiosConfig)
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete<T = any, D = any>(url: string, options: NetworkRequestOptions = {}) {
    return this.sendRequest<T, D>(options, (axiosConfig: AxiosRequestConfig) =>
      axios.delete(url, axiosConfig)
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendRequest<T = any, D = any>(
    options: NetworkRequestOptions,
    requestFn: (axiosConfig: AxiosRequestConfig<D>) => Promise<AxiosResponse<T>>
  ) {
    const axiosConfig = {
      ...DEFAULT_NETWORK_REQ_OPTIONS.networkOptions,
      ...options.networkOptions
    };

    const uiOptions = {
      ...DEFAULT_NETWORK_REQ_OPTIONS.uiOptions,
      ...options.uiOptions
    };

    return of(null)
      .pipe(
        switchMap(() => fromPromise(requestFn(axiosConfig))),
        // Cancel request if the request requires auth and unauthorised error fired
        takeUntil(unauthorisedEventService?.unauthorisedErrorObs.pipe(filter(() => !!options.requireAuth))),
        retryBackoff({
          initialInterval: BACKOFF_INIT_DELAY,
          maxInterval: BACKOFF_MAX_DELAY,
          maxAttempts: 5,
          shouldRetry: error => {
            if (!error.response || !error.response.status || error.config?.method !== 'get') {
              return false;
            }

            return [502, 503, 504].includes(error.response.status);
          }
        }),
        map(res => {
          if (uiOptions.showSuccessMsg) {
            const displayMessage = uiOptions.successMsg || this._getRespMsg(res);

            if (displayMessage) {
              toasterService.pop('success', undefined, displayMessage);
            }
          }

          return res.data;
        }),
        catchError((err: AxiosError) => {
          const errorMsg = getErrorMessageFromAxiosError(err);

          if (errorMsg && this._shouldShowErrorMsg(err, uiOptions) && err.status !== 403) {
            toasterService.pop('error', undefined, errorMsg);
          }

          // Emit unauthorisedErrorObs when an api that requires auth return 401
          if (uiOptions.handleUnauthorisedResponse && err.response?.status === 401) {
            unauthorisedEventService?.unauthorisedErrorObs.next(!!uiOptions.unauthorisedHandleNext);
          }

          return throwError(err);
        })
      )
      .toPromise();
  }

  private _shouldShowErrorMsg(err: AxiosError, uiOptions: NetworkRequestUIOptions): boolean {
    if (err.code === AxiosError.ERR_NETWORK && !uiOptions.handleNetworkError) {
      return false;
    }

    if (err.code === AxiosError.ERR_CANCELED && !uiOptions.handlePromiseCancelError) {
      return false;
    }

    if (uiOptions.handleUnauthorisedResponse && err.response?.status === 401) {
      return true;
    }

    if (!err.response?.status || err.response?.status <= 0) {
      return true;
    }

    return !!uiOptions.showErrorMsg;
  }

  private _getRespMsg(response: AxiosResponse) {
    return response.data
      ? (response.data as { success_message: string }).success_message
      : response.statusText;
  }
}

export const hermesCourierService = new HermesCourierService();
